import { SpotterLayer, SpotterLayerProps } from '@sofarocean/atlas/maps';
import { SpotterMapData } from '@sofarocean/atlas/types';
import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { differenceInMilliseconds } from 'date-fns';
import { isNil } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import scoutImageHover from '@/assets/map/scout-hover.png';
import scoutInactive from '@/assets/map/scout-inactive.png';
import scoutImageSelected from '@/assets/map/scout-selected.png';
import scoutImage from '@/assets/map/scout.png';
import spotterImageYellowHover from '@/assets/map/spotter-yellow-hover.png';
import spotterImageYellowInactive from '@/assets/map/spotter-yellow-inactive.png';
import spotterImageYellowSelected from '@/assets/map/spotter-yellow-selected.png';
import spotterImageYellow from '@/assets/map/spotter-yellow.png';
import { EnrichedMapDevice, SPOTTER_ICONS } from '@/components/map/SpotterMap';
import { INACTIVE_SPOTTER_MS } from '@/config';
import { useMapUiContext } from '@/context/MapUiContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useNow } from '@/hooks/useNow';
import { useSpotterHash } from '@/hooks/useSpotterHash';
import { getSpotterLayerIconName, isScoutDevice, spotterToLngLat } from '@/utils';

export type MySpotterLayerProps = {
    layerId?: string;
    devices: EnrichedMapDevice[];
    /**
     * Called by MapSpotterList to inform containiner when a spotter
     * becomes hovered (or undefined on leaving hover)
     */
    onSpotterHover?: (spotterId: string | undefined) => void;
    /**
     * Spotter that should be given hover treatment
     */
    hoveredSpotterId?: string | undefined;
    /**
     * Spotter that should be given selected treatment
     */
    selectedSpotterId?: string | undefined;
} & Omit<SpotterLayerProps, 'spotterMapData' | 'onSpotterHoverEnter' | 'onSpotterHoverLeave'>;

export const MySpotterLayer = ({
    layerId,
    devices,
    onSpotterHover,
    selectedSpotterId,
    hoveredSpotterId,
    ...props
}: MySpotterLayerProps) => {
    const navigate = useNavigate();
    const { selectedTimestamp } = useTimelineNavigationContext();
    const now = useNow(3600, { enabled: !selectedTimestamp });
    const idleRelativeToEpochMs = selectedTimestamp ? new Date(selectedTimestamp).getTime() : now.getTime();
    const { map } = useContext(MapboxContext);
    const { spotterId, hash } = useSpotterHash();
    const { setMapInspector } = useMapUiContext();
    const [lastPannedHash, setLastPannedHash] = useState<string>();

    useEffect(() => {
        if (!map || lastPannedHash === hash) {
            return;
        }
        const hashDevice = spotterId ? devices?.find((d) => d.spotterId === spotterId) : undefined;
        const hashDeviceLocation = hashDevice?.location ?? hashDevice?.track?.[0];
        if (hashDeviceLocation) {
            setLastPannedHash(hash);
            if (!map.isZooming()) {
                // if we're mid-zoom we're probably setting our initial location, so don't interrupt.
                // we still record this as the last panned hash because we don't want extra jumping next time
                // a dependency changes.
                map.easeTo({ center: spotterToLngLat(hashDeviceLocation) });
            }
            return;
        }
    }, [hash, lastPannedHash, map, setLastPannedHash, spotterId, devices]);

    const spotterMapData = useMemo(() => {
        // sorting out "interacted" and "inactive" spotters from the rest, and then will add from least active to most active to control z-order
        const interactedSpotters: SpotterMapData[] = [];
        const spotters: SpotterMapData[] = [];
        const inactiveSpotters: SpotterMapData[] = [];
        devices.forEach((d) => {
            const isScout = isScoutDevice(d.hullType);
            const { latitude, longitude } = d.location ?? d.track?.[0] ?? {};
            const timestamp = d.location?.timestamp ?? d.track?.[0]?.timestamp;

            if (isNil(latitude) || isNil(longitude)) {
                return;
            }

            const heading = isNil(d.heading) ? 0 : d.heading;
            const spotter = {
                label: d.name || d.spotterId,
                heading,
                position: {
                    lat: latitude,
                    lon: longitude,
                },
                id: d.spotterId,
                timestamp,
                // default icon; may be overwritten depending on selection
                icon: getSpotterLayerIconName('default', isScout),
                wind: d.wind && {
                    ...d.wind,
                    ...d.wind.location,
                },
                waves: d.waves,
            };
            if (d.spotterId === selectedSpotterId) {
                spotter.icon = getSpotterLayerIconName('selected', isScout);
                // selected goes above most spotters, but below hovered
                interactedSpotters.unshift(spotter);
            } else if (d.spotterId === hoveredSpotterId) {
                spotter.icon = getSpotterLayerIconName('highlight', isScout);
                // hovered on top
                interactedSpotters.push(spotter);
            } else if (timestamp && differenceInMilliseconds(idleRelativeToEpochMs, timestamp) <= INACTIVE_SPOTTER_MS) {
                spotters.push(spotter);
            } else {
                spotter.icon = getSpotterLayerIconName('inactive', isScout);
                inactiveSpotters.push(spotter);
            }
        });
        return inactiveSpotters.concat(spotters, interactedSpotters);
    }, [devices, hoveredSpotterId, selectedSpotterId, idleRelativeToEpochMs]);

    return (
        <SpotterLayer
            layerId={layerId}
            spotterColor={SPOTTER_ICONS.mine.color}
            onSpotterClick={(spotterId, spotter) => {
                setMapInspector({
                    type: 'spotter',
                    spotterFamily: 'mine',
                    spotterId: spotterId as string,
                    position: spotter.position,
                });

                // preempt the panner so we don't pan on map click-driven spotter selections
                setLastPannedHash(`#spotter/${spotterId}`);
                navigate(`#spotter/${spotterId}`);
            }}
            onSpotterHoverEnter={(spotterId) => onSpotterHover?.(String(spotterId))}
            onSpotterHoverLeave={() => onSpotterHover?.(undefined)}
            spotterIcons={[
                { name: 'spotter', url: spotterImageYellow },
                { name: 'selected', url: spotterImageYellowSelected },
                { name: 'highlight', url: spotterImageYellowHover },
                { name: 'inactive', url: spotterImageYellowInactive },
                { name: 'scout', url: scoutImage },
                { name: 'scout-selected', url: scoutImageSelected },
                { name: 'scout-highlight', url: scoutImageHover },
                { name: 'scout-inactive', url: scoutInactive },
            ]}
            spotterMapData={spotterMapData}
            {...props}
        />
    );
};
